import React, { useMemo } from 'react';
import PropTypes from 'prop-types';
import { PlusOutlined } from '@ant-design/icons';
import { Row, Col, Table } from 'antd';

import OnTraccrButton from '../../common/buttons/OnTraccrButton';
import BorderlessButton from '../../common/buttons/BorderlessButton';
import RateStack from './RateStack';
import Colors from '../../constants/Colors';
import { sortByLocalCompare } from '../../helpers/helpers';
import {
  billingTypeDisplayName,
  getProjectRateFields,
} from './ProjectBillingRateHelper';

const labourFirstThenName = (a, b) => {
  const typeRank = (type) => (type === 'labour' ? 0 : 1);
  const rankDiff = typeRank(a.type) - typeRank(b.type);
  if (rankDiff !== 0) return rankDiff;
  return sortByLocalCompare('name')(a, b);
};

export default function ProjectBillingRateList({
  rates,
  canEdit,
  onAddFromTemplate,
  onAddNew,
  onEdit,
  onDelete,
}) {
  const sortedRates = useMemo(
    () => [...rates].sort(labourFirstThenName),
    [rates],
  );

  const columns = useMemo(() => {
    const cols = [
      {
        title: 'Name',
        dataIndex: 'name',
        key: 'name',
        ellipsis: true,
      },
      {
        title: 'Type',
        dataIndex: 'type',
        key: 'type',
        render: (type) => billingTypeDisplayName(type),
      },
      {
        title: 'Billing Rates',
        key: 'billing',
        render: (_, record) => (
          <RateStack
            record={record}
            fields={record.type === 'labour'
              ? getProjectRateFields({ type: 'labour', rateType: 'billing' })
              : getProjectRateFields({ type: 'equipment', rateType: 'billing' })}
          />
        ),
      },
      {
        title: 'Cost Rates',
        key: 'cost',
        render: (_, record) => (
          <RateStack
            record={record}
            fields={record.type === 'labour'
              ? getProjectRateFields({ type: 'labour', rateType: 'cost' })
              : getProjectRateFields({ type: 'equipment', rateType: 'cost' })}
          />
        ),
      },
    ];

    if (canEdit) {
      cols.push({
        key: 'actions',
        align: 'right',
        width: 100,
        render: (_, record) => (
          <span
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              justifyContent: 'flex-end',
              whiteSpace: 'nowrap',
              paddingRight: 4,
            }}
          >
            <BorderlessButton
              title=""
              icon="edit"
              color={Colors.ONTRACCR_BLACK}
              onClick={() => onEdit(record)}
            />
            <BorderlessButton
              title=""
              icon="delete"
              color={Colors.ONTRACCR_RED}
              onClick={() => onDelete(record)}
            />
          </span>
        ),
      });
    }
    return cols;
  }, [canEdit, onEdit, onDelete]);

  return (
    <div className="project-add-costcode-container" style={{ paddingLeft: 5, paddingTop: 5 }}>
      {canEdit && (
        <Row
          justify="end"
          style={{ marginBottom: 12 }}
          gutter={8}
          align="middle"
        >
          <Col>
            <OnTraccrButton
              title="Add New"
              icon={<PlusOutlined />}
              onClick={onAddNew}
            />
          </Col>
          <Col>
            <OnTraccrButton
              title="Add From Template"
              icon={<PlusOutlined />}
              onClick={onAddFromTemplate}
            />
          </Col>
        </Row>
      )}
      <Table
        columns={columns}
        dataSource={sortedRates}
        rowKey="id"
        pagination={false}
        size="small"
        scroll
      />
    </div>
  );
}

ProjectBillingRateList.propTypes = {
  rates: PropTypes.array.isRequired,
  canEdit: PropTypes.bool.isRequired,
  onAddFromTemplate: PropTypes.func.isRequired,
  onAddNew: PropTypes.func.isRequired,
  onEdit: PropTypes.func.isRequired,
  onDelete: PropTypes.func.isRequired,
};
